import { Component, OnDestroy, OnInit, NgZone, inject } from '@angular/core';
import { User } from '@angular/fire/auth';
import { VersionEvent } from '@angular/service-worker';
import { Network } from '@capacitor/network';
import { GetNotifications } from '@ih/app/client/shared/components/notifications/data-access';
import {
  MediaQueriesSyncService,
  PushNotificationsService,
  ToastService,
  TrackQueriesService,
  TrackQueriesSyncService,
  UpdateService,
  jobHistoryService,
} from '@ih/app/client/shared/services';
import {
  AuthStateAction,
  LoggedInAction,
  ManuallySetStatus,
  SetStandalone,
  SetStatus,
  SetSynchingStatus,
} from '@ih/app/client/shared/states';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';

import { Platform, ToastController } from '@ionic/angular';
import {
  Actions,
  ofActionCompleted,
  ofActionSuccessful,
  Store,
} from '@ngxs/store';
import { filter, Observable, Subject, take, takeUntil, tap } from 'rxjs';

import { offlineTracker } from './graphql.module';
import { environment } from '../environments/environment';

@Component({
  selector: 'ih-go-root',
  template: ` <ih-app-go-shell-feature></ih-app-go-shell-feature> `,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly updateService = inject(UpdateService);
  private readonly toastController = inject(ToastController);
  private readonly trackQueriesService = inject(TrackQueriesService);
  private readonly trackQueriesSyncService = inject(TrackQueriesSyncService);
  private readonly actions$ = inject(Actions);
  private readonly platform = inject(Platform);
  private readonly toastService = inject(ToastService);
  private readonly mediaQueriesSyncService = inject(MediaQueriesSyncService);
  private readonly pushnotificationsService = inject(PushNotificationsService);
  private zone = inject(NgZone);
  private router = inject(Router);

  updateAvailable$!: Observable<VersionEvent>;

  private ngUnsubscribe = new Subject<void>();

  constructor() {
    const userId$: Observable<string> = this.store.select(
      (state) => state.me.me.id,
    );
    // This had to be added after firebase amdin was updated, now doesnt seem neccesary anymore. Kept just in case issue pops up again.
    // const app = initializeApp(environment.firebase);
    // if (Capacitor.isNativePlatform()) {
    //   initializeAuth(app, {
    //     persistence: indexedDBLocalPersistence
    //   });
    // }

    userId$
      .pipe(
        filter((userId) => !!userId),
        take(1),
      )
      .subscribe((userId) => {
        this.pushnotificationsService.init(
          environment.firebase.vapidKey,
          userId,
        );
      });

    this.initializeApp();
    this.updateAvailable$ = this.updateService.available$().pipe(
      tap((evt) => {
        if (evt.type === 'VERSION_READY') {
          this.updateToast();
        }
      }),
      takeUntil(this.ngUnsubscribe),
    );
  }
  initializeApp() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const domain = 'go.informationhub.io';
        const pathArray = event.url.split(domain);

        const appPath = pathArray.pop();
        if (!appPath) return;

        this.router.navigateByUrl(appPath);
      });
    });
  }

  ngOnInit(): void {
    this.updateAvailable$.subscribe();
    this.store.dispatch(new AuthStateAction());
    this.store.dispatch(new SetStandalone(true));
    this.GetNetworkStatus().then((status) => {
      this.store.dispatch(new SetStatus(status.connected));
    });

    // sync tracked queries on load after logged in and when there is internet
    this.actions$
      .pipe(
        ofActionSuccessful(LoggedInAction),
        tap(() => {
          this.GetNetworkStatus().then((status) => {
            if (status.connected) {
              this.trackQueriesService.removeCompletedQueries().then(() => {
                this.trackQueriesService
                  .getTrackedQueriesFromDB()
                  .then((trackedQueries) => {
                    this.trackQueriesSyncService.syncAll(trackedQueries);
                  });
              });
              this.store.dispatch(new GetNotifications());
            } else {
              offlineTracker.close();
            }
          });
        }),
        take(1),
      )
      .subscribe();

    Network.addListener('networkStatusChange', (status) => {
      this.store.dispatch(new SetStatus(status.connected));

      this.manageOfflineTracker(status.connected);
    });

    this.actions$
      .pipe(
        ofActionCompleted(ManuallySetStatus),
        tap((action: any) => {
          this.manageOfflineTracker(!action.action.newStatus);
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe();
    // setInterval(() => {
    //   console.warn(offlineLink);
    //   console.warn(offlineLinkTracker.getQueue());
    // }, 10000);
  }

  async GetNetworkStatus() {
    return await Network.getStatus();
  }

  async updateToast() {
    const toast = await this.toastController.create({
      header: 'Update Available',
      message: 'Reload the app to receive the new update.',
      position: 'bottom',
      buttons: [
        {
          text: 'Reload',
          role: 'cancel',
          handler: () => {
            /** Only clear localstorage which contains:
             * - NGXS Storage
             * - Apollo Persisted Cache
             * Dexie tables are not cleared due to possibility of losing data that is not synced
             **/
            localStorage.clear();
            window.location.reload();
          },
        },
      ],
    });
    await toast.present();
  }

  manageOfflineTracker(status: boolean) {
    const manualNetwork = this.store.selectSnapshot<boolean>(
      (state) => state.network.manual,
    );

    let synching = this.store.selectSnapshot<boolean>(
      (state) => state.network.synching,
    );

    // TODO : Properly remove auto synching, REMOVE AUTOMATIC SYNCHING properly as users are miss-using it
    synching = false;

    if (status && !manualNetwork) {
      offlineTracker.open();

      const authUser = this.store.selectSnapshot<User | null>(
        (state) => state.authentication.user,
      );
      // sync tracked queries on internet connection when there is no queries in queue
      if (authUser && offlineTracker.getQueue().length === 0) {
        this.trackQueriesService.removeCompletedQueries().then(() => {
          this.trackQueriesService
            .getTrackedQueriesFromDB()
            .then((trackedQueries) => {
              this.trackQueriesSyncService.syncAll(trackedQueries);
            });
        });
      }
      this.store.dispatch(new GetNotifications());
    } else {
      offlineTracker.close();
    }
  }

  ngOnDestroy(): void {
    Network.removeAllListeners();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    // if (!this.apiLoaded.closed) {
    //   this.apiLoaded.closed;
    // }
  }
}
